<template>
  <div class="ret-cards-wrapper">
    <v-card class="ret-header-card">
      <v-container fluid class="pt-0">
        <v-row>
          <v-col>
            <v-text-field clearable hide-details solo
              v-model="search"
              :label="$t('search')"
              append-icon="search"
            />
          </v-col>
          <v-col>
            <v-autocomplete clearable hide-details solo
              v-model="filter.selectedCommand"
              item-text="label"
              item-value="method"
              :label="$t('command')"
              :items="commandIndex"
            />
          </v-col>
          <v-col>
            <v-autocomplete clearable hide-no-data hide-details no-filter solo
              v-model="filter.selectedAssetId"
              item-text="name"
              item-value="id"
              :label="$t('entityNames.assets')"
              :items="assetIndex"
              :search-input.sync="lazySelect['assets'].search"
              :loading="lazySelect['assets'].loading"
              @focus="updateLazySelect({ model: 'assets' })"
            />
          </v-col>
          <v-col>
            <v-autocomplete clearable hide-no-data hide-details no-filter solo
              v-model="filter.selectedUserId"
              item-text="name"
              item-value="id"
              :label="$t('entityNames.users')"
              :items="userIndex"
              :search-input.sync="lazySelect['users'].search"
              :loading="lazySelect['users'].loading"
              @focus="updateLazySelect({ model: 'users' })"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn icon class="mt-2" :title="$t('date')" @click="toggleShowDate">
              <v-icon>access_time</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-if="showDate" class="px-3 text-right">
          <v-spacer />
          <date-picker v-model="filter.selectedTimeStart"
            :time="true"
            :show-label="false"
            :title="$t('time-from')"
            btn-color="black"
          />
          <v-chip label v-if="filter.selectedTimeStart" class="mr-4">
            {{ $t('from') }} {{ filter.selectedTimeStart | formatDate(true) }}
            <v-btn small text icon class="mr-0" @click="clearTimeStart" :title="$t('clear')">
              <v-icon>clear</v-icon>
            </v-btn>
          </v-chip>
          <date-picker v-model="filter.selectedTimeEnd"
            :disabled="!filter.selectedTimeStart"
            :time="true"
            :show-label="false"
            :title="$t('time-to')"
            btn-color="black"
          />
          <v-chip label v-if="filter.selectedTimeEnd">
            {{ $t('to') }} {{ filter.selectedTimeEnd | formatDate(true) }}
            <v-btn small text icon class="mr-0" @click="clearTimeEnd" :title="$t('clear')">
              <v-icon>clear</v-icon>
            </v-btn>
          </v-chip>
        </v-row>
      </v-container>
    </v-card>

    <v-card class="ret-card-new">
      <v-card-text>
        <v-data-table :headers="headers"
                      :items="pagedItems"
                      :server-items-length="totalItems"
                      :footer-props="{ 'items-per-page-text': $t('rows-per-page'), 'items-per-page-options': [10,20,50,100,{ text: $t('all'), value: -1 }] }"
                      :no-data-text="$t('no-data-available')"
                      :options.sync="options"
                      :expanded.sync="expanded"
                      :loading="loading"
        >
          <template v-slot:item="{ item, expand }">
            <tr @click="expand(!expanded.some(i => i.id === item.id))">
              <td>
                <asset-path :item="item.asset" model="assets" clickable-nodes clickable-leaves />
              </td>
              <td>
                {{ translateCommand(item.commandMethod) }}
              </td>
              <td>
                {{ item.commandParams }}
              </td>
              <td>
                {{ item.user | userName }}
              </td>
              <td>
                {{ item.issuedAt | formatDate }}
              </td>
            </tr>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-card flat>
                <v-chip label>
                  <strong class="grey--text text--darken-2">{{ $t('browser') }}</strong>&nbsp;
                  <span>{{ item.computerUserAgent }}</span>
                </v-chip>
                <br />
                <v-chip label>
                  <strong class="grey--text text--darken-2">{{ $t('ip') }}</strong>&nbsp;
                  <span>{{ item.computerIp }}</span>
                </v-chip>
                <br />
                <v-chip label v-if="item.computerName">
                  <strong class="grey--text text--darken-2">{{ $t('computer-name') }}</strong>&nbsp;
                  <span>{{ item.computerName }}</span>
                </v-chip>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.date-picker {
  text-align: right;
}
</style>

<script>
import moment from 'moment'
import lazyItems from '../helpers/lazy-items.es6'
import lazySelects from '../helpers/lazy-selects.es6'
import DatePicker from '../shared/components/ret-date-picker.vue'
import AssetPath from '../shared/components/tree-path.vue'

export default {
  name: 'issued-commands-report',
  mixins: [lazyItems, lazySelects],
  components: {
    AssetPath,
    DatePicker
  },
  data () {
    return {
      model: {
        name: 'shv-command-logs',
        sortColumn: 'issuedAt',
        searchColumns: ['computerName', ['assetParentsView', 'fullPath']]
      },
      autocomplete: [
        { model: 'users', value: 'filter.selectedUserId', search: ['firstname', 'lastname', 'email'] },
        { model: 'assets', value: 'filter.selectedAssetId', search: ['name', ['assetParentsView', 'fullPath']] }
      ],
      dateRange: {

      },
      filter: {
        selectedCommand: null,
        selectedUserId: null,
        selectedAssetId: null,
        selectedTimeStart: null,
        selectedTimeEnd: null
      },
      indexTo: null,
      defaultHeaders: [
        { text: this.$t('entityNames.assets'), value: 'assetParentsView.fullPath', sortable: true },
        { text: this.$t('command'), value: 'commandMethod', sortable: true },
        { text: this.$t('params'), value: 'params', sortable: false },
        { text: this.$t('entityNames.users'), value: 'user', sortable: false },
        { text: this.$t('date'), value: 'issuedAt', sortable: true }
      ],
      expanded: [],
      showDate: false
    }
  },
  computed: {
    commandIndex () {
      if (!this.$store.state.deviceTypes.reduce) {
        return []
      }
      return this.$store.state.deviceTypes
        .reduce((commands, deviceType) => {
          if (!deviceType.commands.length) {
            return commands
          }
          return commands.concat(deviceType.commands)
        }, [])
        .filter((command, i, all) => {
          return all
            .map(command => command.method)
            .indexOf(command.method) === i
        })
        .map((command) => {
          return {
            label: this.translateLabel(command.label),
            method: command.method
          }
        })
    },
    assetIndex () {
      return this.lazySelect['assets'].listItems
        .map((asset) => {
          return {
            id: asset.id,
            name: asset.fullPath || asset.name,
            disabled: asset.disabled
          }
        })
    },
    userIndex () {
      return this.lazySelect['users'].listItems
        .map((user) => {
          return {
            id: user.id,
            name: (user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : `[${user.code}] ${user.email}`),
            disabled: user.disabled
          }
        })
    }
  },
  methods: {
    toggleShowDate () {
      const showDate = !this.showDate
      if (!showDate) {
        this.filter = Object.assign({ ...this.filter }, {
          selectedTimeStart: null,
          selectedTimeEnd: null
        })
      }
      this.showDate = showDate
    },
    toggleRow (index) {
      if (this.expanded.includes(index)) {
        this.expanded = this.expanded.filter((i) => i !== index)
      } else {
        this.expanded = [...this.expanded, index]
      }
    },
    /**
     * @override lazyItems.fetchItems() due to extra filter support
     */
    async fetchItems () {
      try {
        const { name: model, sortColumn, searchColumns } = this.model
        const { sortBy, sortDesc, page, itemsPerPage } = this.options

        const params = {
          limit: itemsPerPage,
          offset: page * itemsPerPage - itemsPerPage
        }

        if (sortBy.length) {
          if (/\./.test(sortBy[0])) {
            params.sort = `${sortBy[0]}+${sortDesc[0] ? 'DESC' : 'ASC'}`
          } else {
            params.sort = `${model}.${sortBy[0]}+${sortDesc[0] ? 'DESC' : 'ASC'}`
          }
        } else if (sortColumn) {
          params.sort = `${model}.${sortColumn}+ASC`
        }

        if (this.search) {
          params.search = searchColumns.map((prop) => {
            if (/string/.test(typeof prop)) {
              return `${model}.${prop}+${this.search}`
            } else {
              const [model, property] = prop
              return `${model}.${property}+${this.search}`
            }
          }).join()
          params.searchMode = 'OR'
        }

        // custom filters
        const filter = []
        const { selectedCommand, selectedAssetId, selectedUserId } = this.filter
        if (selectedCommand) {
          filter.push(`${model}.commandMethod+${selectedCommand}`)
        }
        if (selectedAssetId) {
          filter.push(`${model}.assetId+${selectedAssetId}`)
        }
        if (selectedUserId) {
          filter.push(`${model}.userId+${selectedUserId}`)
        }
        params.filter = filter.join()
        params.filterMode = 'OR'

        const { selectedTimeStart, selectedTimeEnd } = this.filter
        if (selectedTimeStart) {
          params.dateFrom = selectedTimeStart
        }
        if (selectedTimeEnd) {
          params.dateTo = selectedTimeEnd
        }

        const { items, total } = await this.$store.dispatch('lazyItems/getItems', { model, params })

        return Promise.resolve({
          items,
          total
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },
    /**
     * Filters
     */
    clearTimeStart () {
      this.filter.selectedTimeStart = null
      this.filter.selectedTimeEnd = null
    },
    clearTimeEnd () {
      this.filter.selectedTimeEnd = null
    },
    /**
     * Helpers
     */
    translateLabel (label) {
      const prefix = 'assetCommands'
      const translation = this.$t(prefix + '.' + label)
      return translation.indexOf(prefix) === 0
        ? label
        : translation
    },
    translateCommand (method) {
      const command = this.commandIndex.filter((command) => command.method === method).shift()
      if (command) {
        return this.translateLabel(command.label)
      }
      return method
    }
  },
  watch: {
    filter: {
      handler () {
        clearTimeout(this.indexTo)
        this.indexTo = setTimeout(this.updateIndex, 100)
      },
      deep: true
    }
  },
  filters: {
    formatDate (value, short = false) {
      return moment(value).format(short ? 'DD.MM. HH:mm' : 'DD.MM.YYYY HH:mm:ss')
    },
    userName (user) {
      return (user.firstname && user.lastname
        ? `${user.firstname} ${user.lastname}`
        : `[${user.code}] ${user.email}`
      )
    }
  }
}
</script>
